import { SimpleManageSubscriptionsCells } from '../table/ManageSubscriptionsTableBody';
import { ProductEntry, SubscriptionInfoContainer, SubscriptionManagementPageDataOut } from '../../../store/api';
import { SimpleManageSubscriptionTile } from '../table/simple/SimpleManageSubscriptionTile';
import { formatUsageLimit } from '../../../shared/utils/formatUsageLimit';
import { ManageSubscriptionTableCell, SimpleManageSubscriptionTableCell } from '../utils/subscriptionTableTileTypes';
import { mapIssueUsageIndividual } from '../../../shared/subscription-management/mappings/mapIssueUsage';
import { mapProductTitleWarningSymbol } from '../../../shared/subscription-management/mappings/mapProductTitleWarningSymbol';
import { getPatronSubscriptionsLabel } from '../../../shared/subscription-management/utils/getPatronSubscriptionsLabel';
import { issueNotDeliveredYet } from '../../../shared/subscription-management/types/IssueUsage';
import { TFunction } from 'i18next';

export const mapSimpleBody = (
    subscriptions: SubscriptionManagementPageDataOut,
    onAddCopy: (item: ProductEntry) => void,
    onExpireIssues: (item: ProductEntry) => void,
    t: TFunction
): SimpleManageSubscriptionsCells => {
    const list: SimpleManageSubscriptionTile[] = subscriptions.hits.map(subscription => {
        const {
            product,
            currentSubscriptions,
            nextSubscriptions,
            previousIssueStatistics,
            currentIssueStatistics,
            patronSubscriptions,
            expired,
            ceased,
            expirationDate
        } = subscription;

        return {
            product,

            userSubscribedLabel: getPatronSubscriptionsLabel(patronSubscriptions, t),
            showWarning: mapProductTitleWarningSymbol({
                previousLoanStatistics: previousIssueStatistics?.individual,
                currentLoanStatistics: currentIssueStatistics?.individual
            }),

            currentYearSubscriptions: expired
                ? {
                      type: SimpleManageSubscriptionTableCell.Expired,
                      onAddCopy: () => onAddCopy(subscription),
                      onExpireIssues: () => onExpireIssues(subscription),
                      expirationDate
                  }
                : ceased
                ? {
                      type: SimpleManageSubscriptionTableCell.Ceased,
                      onAddCopy: () => onAddCopy(subscription),
                      onExpireIssues: () => onExpireIssues(subscription),
                      expirationDate
                  }
                : currentSubscriptions && currentSubscriptions.individualUsageLimit != null
                ? currentSubscriptions.individualUsageLimit > 0
                    ? {
                          type: SimpleManageSubscriptionTableCell.UsageLimitValue,
                          label: formatUsageLimit(t, currentSubscriptions.individualUsageLimit),
                          onAddCopy: () => onAddCopy(subscription),
                          stockDetails: currentSubscriptions.stockDetails.overview
                      }
                    : {
                          type: SimpleManageSubscriptionTableCell.UsageLimitUnlimited,
                          label: formatUsageLimit(t, currentSubscriptions.individualUsageLimit),
                          stockDetails: currentSubscriptions.stockDetails.overview
                      }
                : {
                      type: SimpleManageSubscriptionTableCell.UsageLimitNone,
                      label: t('shop.subscriptions.noCurrentYearSubscription'),
                      onAddCopy: () => onAddCopy(subscription)
                  },

            previousIssueUsage: hasSubscription(currentSubscriptions)
                ? previousIssueStatistics.individual
                    ? mapIssueUsageIndividual(previousIssueStatistics.individual)
                    : issueNotDeliveredYet(t)
                : undefined,

            currentIssueUsage: hasSubscription(currentSubscriptions)
                ? currentIssueStatistics.individual
                    ? mapIssueUsageIndividual(currentIssueStatistics.individual)
                    : issueNotDeliveredYet(t)
                : undefined,

            nextYearSubscriptions:
                nextSubscriptions && nextSubscriptions.individualUsageLimit != null
                    ? {
                          type: ManageSubscriptionTableCell.UsageLimitValue,
                          label: formatUsageLimit(t, nextSubscriptions.individualUsageLimit),
                          stockDetails: nextSubscriptions.stockDetails.overview
                      }
                    : {
                          type: ManageSubscriptionTableCell.UsageLimitNone,
                          label: t('shop.subscriptions.notRenewedYet')
                      },

            expired,
            ceased
        };
    });

    return {
        type: 'SimpleManageSubscriptionsCells',
        list
    };
};

const hasSubscription = (currentYearSubscription?: SubscriptionInfoContainer) =>
    currentYearSubscription?.individualUsageLimit != null;
